import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Building2, Lock } from "lucide-react";
import { useSeo } from "../components/site/Layout";
import { PageHero, Section, ThirdPartyNotice, CtaBand } from "../components/site/blocks";
import { api } from "../lib/api";
import { euros } from "../data/site";

const ForfaitCard = ({ f, featured }) => (
  <article data-testid={`forfait-card-${f.slug}`} className={`rounded-[28px] p-7 sm:p-8 flex flex-col border ${featured ? "bg-navy text-mist border-navy" : "bg-white border-navy/10"}`}>
    <h2 className={`text-2xl leading-snug ${featured ? "text-mist" : ""}`}>{f.name}</h2>
    <p className={`mt-3 ${featured ? "text-mist/85" : "text-slate-700"}`}>{f.description}</p>
    <p className="mt-6 flex items-baseline gap-2">
      <span className={`font-display text-5xl ${featured ? "text-mist" : "text-navy"}`} data-testid={`forfait-price-${f.slug}`}>{euros(f.price_cents)}</span>
      <span className={`text-sm font-semibold ${featured ? "text-mist/75" : "text-slate-600"}`}>TTC</span>
    </p>
    <ul className="mt-6 space-y-2.5 flex-1">
      {f.features.map((x) => (
        <li key={x} className="flex gap-2 items-start"><Check className={`h-5 w-5 mt-0.5 shrink-0 ${featured ? "text-brand-200" : "text-brand-ink"}`} aria-hidden="true" /><span className={featured ? "text-mist/90" : "text-slate-700"}>{x}</span></li>
      ))}
    </ul>
    <Link to={`/tarifs/${f.id}/paiement`} className={`mt-8 ${featured ? "btn-accent" : "btn-primary"}`} data-testid={`forfait-pay-${f.slug}`}>
      Payer en ligne <ArrowRight className="h-5 w-5" aria-hidden="true" />
    </Link>
  </article>
);

export default function Tarifs() {
  useSeo("Tarifs et forfaits", "Forfaits Habitalys : diagnostic autonomie à domicile, étude d'éligibilité et montage de dossier d'aides, conseil rénovation énergétique. Prix TTC, paiement sécurisé par carte.");
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/forfaits").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  return (
    <>
      <PageHero eyebrow="Tarifs" title="Des forfaits clairs, payables en ligne"
        intro="Pour les prestations les plus courantes, nous proposons des forfaits à prix fixe TTC. Le paiement par carte est sécurisé par Stripe ; vous recevez une confirmation par email et un conseiller vous recontacte sous 48 h ouvrées." />
      <Section testid="tarifs-list">
        {!items && <p className="text-slate-600">Chargement des forfaits…</p>}
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">
          {items?.map((f, i) => <ForfaitCard key={f.id} f={f} featured={i === 0} />)}
          <article data-testid="forfait-card-amo-sur-devis" className="rounded-[28px] p-7 sm:p-8 flex flex-col border-2 border-dashed border-navy/25 bg-mist-50">
            <Building2 className="h-8 w-8 text-brand-ink" aria-hidden="true" />
            <h2 className="mt-4 text-2xl leading-snug">Mission AMO professionnels — sur devis</h2>
            <p className="mt-3 text-slate-700 flex-1">Bailleurs sociaux, collectivités, établissements médico-sociaux, copropriétés : chaque mission d'assistance à maîtrise d'ouvrage fait l'objet d'une proposition adaptée à votre programme.</p>
            <p className="mt-6 font-display text-3xl text-navy">Sur devis</p>
            <Link to="/contact?profil=professionnel&besoin=AMO%20%26%20ma%C3%AEtrise%20d'ouvrage%20d%C3%A9l%C3%A9gu%C3%A9e" className="btn-outline mt-8" data-testid="forfait-amo-quote-cta">Demander un devis</Link>
          </article>
        </div>
        <div className="mt-10 grid lg:grid-cols-2 gap-6">
          <p className="flex gap-3 items-start rounded-2xl bg-mist p-5 text-slate-800"><Lock className="h-6 w-6 text-brand-ink shrink-0" aria-hidden="true" />
            Paiement par carte bancaire via Stripe. Habitalys n'a jamais accès à vos coordonnées bancaires. Prix TTC ; droit de rétractation de 14 jours pour les particuliers, selon nos <Link to="/cgv" className="link-u text-navy font-semibold">CGV</Link>.</p>
          <ThirdPartyNotice />
        </div>
        <p className="mt-6 text-slate-600">Ces forfaits ne comprennent pas le coût des travaux, facturés directement par les entreprises intervenantes. Les aides éventuelles restent indicatives, sous réserve d'éligibilité et des règles en vigueur.</p>
      </Section>
      <CtaBand title="Un besoin qui sort du cadre d'un forfait ?" text="Décrivez-nous votre projet : nous vous adressons un devis personnalisé sous 48 h ouvrées." />
    </>
  );
}
